import { BankAccountEntityType } from "@/lib/supabase/supabaseTypes";
import {
	PaymentSlipDataInput,
	validatePaymentSlipData,
} from "../../../../pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { AddressRecipient } from "../../../../pdf-templates/utils";
import { PaymentReminderDocument } from "../payment-reminder.types";

export const OPEN_EPSILON = 0.001;

// Fee invoices don't exist before the stage is booked; any native number validates the same.
const PREFLIGHT_FEE_DOC_NUMBER = "000001";

// Migrated 'M-…' numbers can't form a QR reference, so those invoices get no slip.
const hasQrReference = (docNumber: string) => /^\d+$/.test(docNumber);

const slipItems = ({ invoice, fees }: PaymentReminderDocument) =>
	[
		{ docNumber: invoice.doc_number, amount: invoice.open_amount },
		...fees.map((fee) => ({
			docNumber: fee.doc_number,
			amount: fee.open_amount,
		})),
	].filter(
		({ docNumber, amount }) =>
			amount > OPEN_EPSILON && hasQrReference(docNumber)
	);

export const hasReminderSlips = (document: PaymentReminderDocument) =>
	slipItems(document).length > 0;

// Slip 1 keeps the dunned invoice's reference so CAMT54 still matches the invoice;
// every open fee invoice gets its own slip under the fee invoice's reference.
export function buildReminderPaymentSlips(
	document: PaymentReminderDocument,
	bankAccount: BankAccountEntityType
): PaymentSlipDataInput[] {
	const currency = document.invoice.currency || "CHF";
	return slipItems(document).map(({ docNumber, amount }) => ({
		bankAccount,
		recipient: document.recipient,
		amount: amount.toFixed(2),
		currency,
		invoiceNumber: docNumber,
	}));
}

export function getReminderSlipError(
	document: PaymentReminderDocument,
	bankAccount: BankAccountEntityType
): string | null {
	for (const slip of buildReminderPaymentSlips(document, bankAccount)) {
		const { error } = validatePaymentSlipData(slip);
		if (error) return error;
	}
	return null;
}

export interface ReminderSlipPreflightInput {
	docNumber: string;
	openAmount: number;
	needsFeeSlip: boolean;
	currency: string;
	bankAccount: BankAccountEntityType | null;
	recipient: AddressRecipient | null;
}

// Checked before a stage is booked, so a letter never ends up with a broken slip.
export function getReminderSlipPreflightError({
	docNumber,
	openAmount,
	needsFeeSlip,
	currency,
	bankAccount,
	recipient,
}: ReminderSlipPreflightInput): string | null {
	const invoiceSlip = hasQrReference(docNumber) && openAmount > OPEN_EPSILON;
	if (!invoiceSlip && !needsFeeSlip) return null;
	if (!bankAccount) return "Kein Bankkonto für den Zahlschein hinterlegt.";
	if (!recipient) return "Die Empfängeradresse der Rechnung fehlt.";
	const { error } = validatePaymentSlipData({
		bankAccount,
		recipient,
		amount: (invoiceSlip ? openAmount : 1).toFixed(2),
		currency,
		invoiceNumber: invoiceSlip ? docNumber : PREFLIGHT_FEE_DOC_NUMBER,
	});
	return error || null;
}
